import { Inbox } from "lucide-react";
import type { ReactNode } from "react";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { formatDashboardCount } from "@/features/dashboard/metricFormatters";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import type {
  DashboardAvailability,
  DashboardCountMetric,
  DashboardOperationalCountMetric,
  DashboardSectionStatus,
  OverviewerDashboardAnalytics,
  OverviewerDashboardOperations,
} from "@/features/dashboard/types";
import { DashboardFrame } from "@/components/dashboard/ledger/DashboardFrame";
import { DashboardLoadSituation } from "@/components/dashboard/ledger/DashboardLoadSituation";
import { DashboardMetricStrip } from "@/components/dashboard/ledger/DashboardMetricStrip";
import { DashboardOldestLoads } from "@/components/dashboard/ledger/DashboardOldestLoads";
import { DashboardQualityOutcomes } from "@/components/dashboard/ledger/DashboardQualityOutcomes";
import { DashboardQuantityBreakdown } from "@/components/dashboard/ledger/DashboardQuantityBreakdown";
import type { DashboardRankingRowModel } from "@/components/dashboard/ledger/DashboardRankingDrawer";
import { DashboardRankingPreview } from "@/components/dashboard/ledger/DashboardRankingPreview";
import { DashboardSection } from "@/components/dashboard/ledger/DashboardSection";
import { DashboardState } from "@/components/dashboard/ledger/DashboardState";

const OVERVIEWER_DASHBOARD_CSS = `
  .overviewer-dashboard {
    min-width: 0;
    color: var(--ledger-text);
    font-variant-numeric: tabular-nums;
  }

  .overviewer-dashboard__stack {
    display: grid;
    gap: 18px;
    min-width: 0;
  }

  .overviewer-dashboard__operations {
    align-items: stretch;
    display: grid;
    gap: 14px;
    grid-template-columns: minmax(0, 1.2fr) minmax(18rem, 0.8fr);
    min-width: 0;
  }

  .overviewer-dashboard__operations > * { height: 100%; }

  .overviewer-dashboard__lower {
    align-items: stretch;
    display: grid;
    gap: 14px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-width: 0;
  }

  .overviewer-dashboard__unavailable {
    align-items: center;
    min-height: 7rem;
    padding-block: 0.75rem;
    border-block: 1px solid var(--ledger-border);
    color: var(--ledger-muted);
    display: flex;
    font-family: "DM Sans", sans-serif;
    font-size: 0.88rem;
    gap: 0.55rem;
  }

  @media (max-width: 900px) {
    .overviewer-dashboard__operations,
    .overviewer-dashboard__lower {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  @media (max-width: 390px) {
    .overviewer-dashboard__stack { gap: 14px; }
  }

`;

export type OverviewerDashboardNavigation = { to: "/cargas" };

type CheckerRankingReturnTarget = {
  kind: "checkers";
  sort: "checked_items";
};

export type OverviewerDashboardPanelRequest =
  | {
      mode: "ranking";
      kind: "checkers";
      sort: "checked_items";
      title: string;
    }
  | {
      mode: "detail";
      kind: "checker";
      id: string;
      title: string;
      returnTo: CheckerRankingReturnTarget;
    }
  | {
      mode: "detail";
      kind: "load";
      id: string;
      title: string;
    }
  | { mode: "quantities" }
  | { mode: "quality" };

export interface OverviewerDashboardProps {
  user: CurrentUser;
  analytics: OverviewerDashboardAnalytics;
  operations?: OverviewerDashboardOperations;
  period: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  updating?: boolean;
  onPeriodChange: (period: DashboardPeriodState) => void;
  onNavigate: (destination: OverviewerDashboardNavigation) => void;
  onOpenPanel: (request: OverviewerDashboardPanelRequest) => void;
  onRetryAnalytics: () => void;
}

function OverviewerAnalyticsSection({
  status,
  title,
  onRetry,
  children,
}: {
  status?: DashboardSectionStatus;
  title: string;
  onRetry: () => void;
  children: ReactNode;
}) {
  if (!status || status.status === "available") return children;
  const failed = status.status === "error";
  return (
    <DashboardSection title={title}>
      <DashboardState
        status={failed ? "error" : "empty"}
        scope="section"
        title={failed ? "Falha ao carregar esta seção" : "Seção indisponível"}
        message={
          failed
            ? "Não foi possível carregar os dados desta seção."
            : "Dados indisponíveis para esta seção."
        }
        onRetry={failed ? onRetry : undefined}
      />
    </DashboardSection>
  );
}

const analyticsSectionStatus = (
  analytics: OverviewerDashboardAnalytics,
  section: DashboardSectionStatus["section"],
) => analytics.section_status?.find((status) => status.section === section);

interface CheckerPreviewRow extends DashboardRankingRowModel {
  request: Extract<OverviewerDashboardPanelRequest, { mode: "detail" }>;
}

const rankingRequest = {
  mode: "ranking",
  kind: "checkers",
  sort: "checked_items",
  title: "Comparativo por conferente",
} as const;

const asCountMetric = (
  metric: DashboardOperationalCountMetric,
): DashboardCountMetric =>
  metric.status === "available"
    ? {
        status: "available",
        unit: "count",
        value: metric.count,
        comparison: metric.comparison,
      }
    : {
        status: metric.status,
        unit: "count",
        reason_code: metric.reason_code,
      };

function currentOpenLoadsMetric(
  analytics: OverviewerDashboardAnalytics,
  operations: OverviewerDashboardOperations | undefined,
): DashboardCountMetric {
  if (!operations) return asCountMetric(analytics.metrics.open_loads);
  if (operations.projection_status === "not_ready") {
    return {
      status: "unavailable",
      unit: "count",
      reason_code: "projection_not_ready",
    };
  }
  const status = operations.section_status?.find(
    ({ section }) => section === "open_loads",
  );
  if (status && status.status !== "available") {
    return {
      status: status.status,
      unit: "count",
      reason_code: status.reason_code,
    };
  }
  return {
    status: "available",
    unit: "count",
    value: operations.open_loads.total,
  };
}

function oldestLoadsState(
  operations: OverviewerDashboardOperations | undefined,
) {
  if (!operations || operations.projection_status === "not_ready") {
    return { status: "unavailable" as const, loads: [] };
  }
  const status = operations.section_status?.find(
    ({ section }) => section === "oldest_open",
  );
  return {
    status: (status?.status ?? "available") as DashboardAvailability,
    loads:
      status && status.status !== "available" ? [] : operations.oldest_open,
  };
}

const checkerRows = (
  items: OverviewerDashboardAnalytics["sections"]["checker_highlights"],
): CheckerPreviewRow[] =>
  items.map((item) => {
    const checkedItems = formatDashboardCount(item.checked_items);
    const divergences = formatDashboardCount(item.divergences);
    const concludedLoads = formatDashboardCount(item.concluded_loads);
    return {
      id: item.checker_id,
      name: item.checker_name,
      metrics: [
        {
          label: "Itens",
          value: checkedItems.full,
          accessibleLabel: `Itens: ${checkedItems.accessibleLabel}`,
        },
        {
          label: "Divergências",
          value: divergences.full,
          accessibleLabel: `Divergências: ${divergences.accessibleLabel}`,
        },
        {
          label: "Cargas concluídas",
          value: concludedLoads.full,
          accessibleLabel: `Cargas concluídas: ${concludedLoads.accessibleLabel}`,
        },
      ],
      request: {
        mode: "detail",
        kind: "checker",
        id: item.checker_id,
        title: item.checker_name,
        returnTo: { kind: "checkers", sort: "checked_items" },
      },
    };
  });

export function OverviewerDashboard({
  user,
  analytics,
  operations,
  period,
  pendingPeriod,
  updating = false,
  onPeriodChange,
  onNavigate,
  onOpenPanel,
  onRetryAnalytics,
}: OverviewerDashboardProps) {
  const openCargas = () => onNavigate({ to: "/cargas" });
  const oldest = oldestLoadsState(operations);
  const quantities =
    analytics.metrics.checked_quantities.status === "available"
      ? analytics.metrics.checked_quantities.quantities
      : [];

  return (
    <DashboardFrame
      title="Painel de supervisão"
      className="overviewer-dashboard"
      period={period}
      pendingPeriod={pendingPeriod}
      periodMetadata={analytics.period}
      onPeriodChange={onPeriodChange}
      updating={updating}
    >
      <style>{OVERVIEWER_DASHBOARD_CSS}</style>
      <div
        className="overviewer-dashboard__stack"
        data-dashboard-role={user.role}
      >
        <div
          data-overviewer-block="headlines"
          data-testid="overviewer-dashboard-headlines"
        >
          <DashboardMetricStrip
            ariaLabel="Indicadores operacionais globais"
            items={[
              {
                id: "checked-loads",
                label: "Cargas conferidas",
                metric: asCountMetric(analytics.metrics.checked_loads),
                onSelect: openCargas,
              },
              {
                id: "open-loads",
                label: "Cargas abertas",
                metric: currentOpenLoadsMetric(analytics, operations),
                onSelect: openCargas,
              },
              {
                id: "checked-items",
                label: "Itens conferidos",
                metric: asCountMetric(analytics.metrics.checked_items),
                onSelect: () => onOpenPanel({ mode: "quantities" }),
              },
              {
                id: "divergences",
                label: "Divergências",
                metric: asCountMetric(analytics.metrics.divergences),
                comparisonGoal: "lower-is-better",
                onSelect: () => onOpenPanel({ mode: "quality" }),
              },
            ]}
          />
        </div>

        <div
          className="overviewer-dashboard__operations"
          data-overviewer-block="oldest-and-situation"
          data-testid="overviewer-dashboard-operations"
          data-desktop-layout="side-by-side"
          data-mobile-layout="stacked"
        >
          <DashboardOldestLoads
            loads={oldest.loads}
            status={oldest.status}
            onViewAll={openCargas}
            onOpenLoad={(load) =>
              onOpenPanel({
                mode: "detail",
                kind: "load",
                id: load.order_id,
                title: load.label,
              })
            }
          />
          <OverviewerAnalyticsSection
            status={analyticsSectionStatus(analytics, "load_situation")}
            title="Situação das cargas"
            onRetry={onRetryAnalytics}
          >
            <DashboardLoadSituation
              mode="operational"
              situation={analytics.sections.load_situation}
              onSelectState={openCargas}
            />
          </OverviewerAnalyticsSection>
        </div>

        <div
          data-overviewer-block="checker-comparison"
          data-testid="overviewer-dashboard-checkers"
        >
          <OverviewerAnalyticsSection
            status={analyticsSectionStatus(analytics, "checker_highlights")}
            title="Comparativo por conferente"
            onRetry={onRetryAnalytics}
          >
            <DashboardRankingPreview
              title="Comparativo por conferente"
              items={checkerRows(analytics.sections.checker_highlights)}
              openAllAriaLabel="Ver todos em Comparativo por conferente"
              onOpenAll={() => onOpenPanel(rankingRequest)}
              onOpenItem={(row) => onOpenPanel(row.request)}
            />
          </OverviewerAnalyticsSection>
        </div>

        <div
          className="overviewer-dashboard__lower"
          data-overviewer-block="quantities-and-outcomes"
          data-testid="overviewer-dashboard-lower"
        >
          <DashboardSection
            title="Quantidades conferidas"
            action={
              <button
                type="button"
                className="dashboard-ledger-action"
                aria-label="Ver detalhes de quantidades conferidas"
                onClick={() => onOpenPanel({ mode: "quantities" })}
              >
                Ver detalhes
              </button>
            }
          >
            {analytics.metrics.checked_quantities.status === "available" ? (
              <DashboardQuantityBreakdown
                quantities={quantities}
                ariaLabel="Quantidades conferidas"
                onSelect={() => onOpenPanel({ mode: "quantities" })}
              />
            ) : (
              <div className="overviewer-dashboard__unavailable" role="status">
                <Inbox aria-hidden="true" size={18} />
                Esta seção está indisponível.
              </div>
            )}
          </DashboardSection>
          <OverviewerAnalyticsSection
            status={analyticsSectionStatus(analytics, "divergence_outcomes")}
            title="Resultado das divergências"
            onRetry={onRetryAnalytics}
          >
            <DashboardSection
              title="Resultado das divergências"
              action={
                <button
                  type="button"
                  className="dashboard-ledger-action"
                  aria-label="Ver detalhes do resultado das divergências"
                  onClick={() => onOpenPanel({ mode: "quality" })}
                >
                  Ver detalhes
                </button>
              }
            >
              <DashboardQualityOutcomes
                outcomes={analytics.sections.divergence_outcomes}
                ariaLabel="Resultado das divergências"
                onSelect={() => onOpenPanel({ mode: "quality" })}
              />
            </DashboardSection>
          </OverviewerAnalyticsSection>
        </div>
      </div>
    </DashboardFrame>
  );
}
